import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from './Sidebar';
import { motion, AnimatePresence } from 'framer-motion';
import {
    FaTasks, FaPlus, FaTimes, FaCheck, FaEdit, FaTrash,
    FaCalendarAlt, FaUserAlt, FaLightbulb, FaArrowRight,
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import './Dashboard.css';
import './AdminTasks.css';

const API = 'https://aayushrealproperties.in/nimaibackend/public/api';

const today = () => new Date().toISOString().split('T')[0];

const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const PRIORITY_META = {
    high:   { label: 'High',   cls: 'priority-high',   icon: '🔴' },
    medium: { label: 'Medium', cls: 'priority-medium', icon: '🟡' },
    low:    { label: 'Low',    cls: 'priority-low',    icon: '🟢' },
};

const STATUS_COLS = [
    { key: 'todo',        label: 'To Do',       cls: 'col-todo'     },
    { key: 'in_progress', label: 'In Progress', cls: 'col-progress' },
    { key: 'done',        label: 'Done',        cls: 'col-done'     },
];

const EMPTY_FORM = {
    title: '', description: '', assigned_to: '', priority: 'medium',
    status: 'todo', due_date: today(), feature_request_id: '', client_id: '',
};

const parseJson = async (res) => {
    const text = await res.text();
    const i = text.search(/\{|\[/);
    return JSON.parse(i !== -1 ? text.substring(i) : text);
};

const AdminTasks = () => {
    const [tasks, setTasks] = useState([]);
    const [features, setFeatures] = useState([]);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);

    // Filters
    const [filterDate, setFilterDate] = useState('');
    const [filterStatus] = useState('');
    const [filterAssignee, setFilterAssignee] = useState('');
    const [view, setView] = useState('kanban'); // kanban | list

    // Modal
    const [showForm, setShowForm] = useState(false);
    const [editingTask, setEditingTask] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [deletingId, setDeletingId] = useState('');

    const token = localStorage.getItem('admin_token');
    const headers = { Authorization: `Bearer ${token}`, Accept: 'application/json' };

    const fetchTasks = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filterDate) params.set('date', filterDate);
            if (filterStatus) params.set('status', filterStatus);
            const res = await fetch(`${API}/admin/tasks?${params}`, { headers });
            const json = await parseJson(res);
            if (json.status) setTasks(json.data || []);
        } catch { toast.error('Failed to load tasks'); }
        finally { setLoading(false); }
    }, [filterDate, filterStatus]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { fetchTasks(); }, [fetchTasks]);

    useEffect(() => {
        // Fetch feature requests for dropdown
        fetch(`${API}/admin/feature-requests`, { headers })
            .then(r => r.text()).then(t => {
                const i = t.search(/\{|\[/);
                const json = JSON.parse(i !== -1 ? t.substring(i) : t);
                if (json.status) setFeatures(json.data || []);
            }).catch(() => {});

        // Fetch clients for dropdown
        fetch(`${API}/clients`, { headers })
            .then(r => r.text()).then(t => {
                const i = t.search(/\{|\[/);
                const json = JSON.parse(i !== -1 ? t.substring(i) : t);
                if (json.status) setClients(json.data || []);
            }).catch(() => {});
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const openCreate = (status = 'todo', featureId = '', clientId = '', featureTitle = '') => {
        setEditingTask(null);
        setForm({
            ...EMPTY_FORM,
            status,
            feature_request_id: featureId,
            client_id: clientId,
            title: featureTitle ? `Work on: ${featureTitle}` : '',
        });
        setShowForm(true);
    };

    const openEdit = (task) => {
        setEditingTask(task);
        setForm({
            title: task.title || '',
            description: task.description || '',
            assigned_to: task.assigned_to || '',
            priority: task.priority || 'medium',
            status: task.status || 'todo',
            due_date: task.due_date || today(),
            feature_request_id: task.feature_request_id || '',
            client_id: task.client_id || '',
        });
        setShowForm(true);
    };

    const handleSave = async () => {
        if (!form.title.trim()) { toast.error('Title is required'); return; }
        setSaving(true);
        try {
            const url = editingTask ? `${API}/admin/tasks/${editingTask.id}` : `${API}/admin/tasks`;
            const method = editingTask ? 'PUT' : 'POST';
            const res = await fetch(url, {
                method,
                headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...form,
                    feature_request_id: form.feature_request_id || null,
                    client_id: form.client_id || null,
                }),
            });
            const json = await parseJson(res);
            if (json.status) {
                toast.success(editingTask ? 'Task updated' : 'Task created');
                setShowForm(false);
                fetchTasks();
            } else {
                toast.error('Failed to save task');
            }
        } catch { toast.error('Network error'); }
        finally { setSaving(false); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this task?')) return;
        setDeletingId(id);
        try {
            await fetch(`${API}/admin/tasks/${id}`, { method: 'DELETE', headers });
            setTasks(prev => prev.filter(t => t.id !== id));
            toast.success('Task deleted');
        } catch { toast.error('Network error'); }
        finally { setDeletingId(''); }
    };

    const moveStatus = async (task, newStatus) => {
        try {
            await fetch(`${API}/admin/tasks/${task.id}`, {
                method: 'PUT',
                headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify({ status: newStatus }),
            });
            setTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: newStatus } : t));
        } catch { toast.error('Network error'); }
    };

    // Derived
    const assignees = [...new Set(tasks.map(t => t.assigned_to).filter(Boolean))];
    const filtered = tasks.filter(t =>
        (!filterAssignee || t.assigned_to === filterAssignee)
    );

    const counts = { todo: 0, in_progress: 0, done: 0 };
    filtered.forEach(t => { if (counts[t.status] !== undefined) counts[t.status]++; });

    // Features with no task yet (pending or in_progress — not completed/rejected)
    const unassignedFeatures = features.filter(f =>
        ['pending', 'in_progress'].includes(f.status) &&
        !tasks.some(t => t.feature_request_id === f.id)
    );

    return (
        <div className="admin-layout">
            <Sidebar />
            <main className="admin-content">
                <header className="content-header">
                    <div>
                        <h1>Daily <span>Tasks</span></h1>
                        <p>Plan and track your team's daily work based on client feature requests.</p>
                    </div>
                    <button className="task-new-btn" onClick={() => openCreate()}>
                        <FaPlus /> New Task
                    </button>
                </header>

                {/* Stats Row */}
                <div className="task-stats-row">
                    <div className="task-stat todo">
                        <div className="task-stat-icon"><FaTasks /></div>
                        <div className="task-stat-body">
                            <div className="task-stat-num">{counts.todo}</div>
                            <div className="task-stat-lbl">To Do</div>
                        </div>
                    </div>
                    <div className="task-stat inprogress">
                        <div className="task-stat-icon"><FaArrowRight /></div>
                        <div className="task-stat-body">
                            <div className="task-stat-num">{counts.in_progress}</div>
                            <div className="task-stat-lbl">In Progress</div>
                        </div>
                    </div>
                    <div className="task-stat done">
                        <div className="task-stat-icon"><FaCheck /></div>
                        <div className="task-stat-body">
                            <div className="task-stat-num">{counts.done}</div>
                            <div className="task-stat-lbl">Done Today</div>
                        </div>
                    </div>
                    <div className="task-stat pending-feat">
                        <div className="task-stat-icon"><FaLightbulb /></div>
                        <div className="task-stat-body">
                            <div className="task-stat-num">{unassignedFeatures.length}</div>
                            <div className="task-stat-lbl">Pending Features</div>
                        </div>
                    </div>
                </div>

                {/* Unassigned Feature Requests Banner */}
                {unassignedFeatures.length > 0 && (
                    <div className="feat-banner">
                        <div className="feat-banner-title"><FaLightbulb /> Feature requests without a task — convert to daily work:</div>
                        <div className="feat-banner-list">
                            {unassignedFeatures.map(f => (
                                <div key={f.id} className="feat-banner-item">
                                    <div className="feat-banner-info">
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <span className="feat-banner-name">{f.title}</span>
                                            {f.status === 'in_progress' && (
                                                <span style={{ fontSize: '0.72rem', fontWeight: 700, background: '#eff6ff', color: '#3b82f6', padding: '2px 8px', borderRadius: 20 }}>In Progress</span>
                                            )}
                                        </div>
                                        <span className="feat-banner-client">{f.first_name} {f.last_name} · {f.project_name}</span>
                                    </div>
                                    <button
                                        className="feat-banner-btn"
                                        onClick={() => openCreate('todo', f.id, f.client_id, f.title)}
                                    >
                                        <FaArrowRight /> Create Task
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Filters */}
                <div className="task-filters">
                    <div className="task-filter-group">
                        <FaCalendarAlt />
                        <input
                            type="date"
                            className="task-filter-input"
                            value={filterDate}
                            onChange={e => setFilterDate(e.target.value)}
                            title="Filter by due date"
                        />
                        {filterDate && (
                            <button className="task-filter-clear" onClick={() => setFilterDate('')}>
                                <FaTimes />
                            </button>
                        )}
                    </div>
                    <div className="task-filter-group">
                        <FaUserAlt />
                        <select
                            className="task-filter-input"
                            value={filterAssignee}
                            onChange={e => setFilterAssignee(e.target.value)}
                        >
                            <option value="">All Assignees</option>
                            {assignees.map(a => <option key={a} value={a}>{a}</option>)}
                        </select>
                    </div>
                    <div className="task-view-toggle">
                        <button className={view === 'kanban' ? 'active' : ''} onClick={() => setView('kanban')}>Kanban</button>
                        <button className={view === 'list' ? 'active' : ''} onClick={() => setView('list')}>List</button>
                    </div>
                </div>

                {loading ? (
                    <div className="clients-loading"><p>Loading tasks…</p></div>
                ) : view === 'kanban' ? (
                    /* ─── KANBAN VIEW ─── */
                    <div className="kanban-board">
                        {STATUS_COLS.map(col => {
                            const colTasks = filtered.filter(t => t.status === col.key);
                            return (
                                <div key={col.key} className={`kanban-col ${col.cls}`}>
                                    <div className="kanban-col-header">
                                        <span className="kanban-col-title">{col.label}</span>
                                        <span className="kanban-col-count">{colTasks.length}</span>
                                        <button
                                            className="kanban-add-btn"
                                            title="Add task here"
                                            onClick={() => openCreate(col.key)}
                                        >
                                            <FaPlus />
                                        </button>
                                    </div>
                                    <div className="kanban-col-body">
                                        <AnimatePresence>
                                            {colTasks.length === 0 && (
                                                <div className="kanban-empty">
                                                    <div className="kanban-empty-icon">
                                                        {col.key === 'todo' ? '📋' : col.key === 'in_progress' ? '⚡' : '✅'}
                                                    </div>
                                                    <span>No tasks here</span>
                                                    <span style={{fontSize:'0.75rem', color:'#e2e8f0'}}>Click + to add one</span>
                                                </div>
                                            )}
                                            {colTasks.map(task => (
                                                <motion.div
                                                    key={task.id}
                                                    layout
                                                    initial={{ opacity: 0, y: 10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    exit={{ opacity: 0, scale: 0.95 }}
                                                    className={`task-card priority-border-${task.priority}`}
                                                >
                                                    <div className="task-card-top">
                                                        <span className={`task-priority-pill ${PRIORITY_META[task.priority]?.cls}`}>
                                                            {PRIORITY_META[task.priority]?.icon} {PRIORITY_META[task.priority]?.label}
                                                        </span>
                                                        <div className="task-card-actions">
                                                            <button onClick={() => openEdit(task)} title="Edit"><FaEdit /></button>
                                                            <button
                                                                className="task-card-del del"
                                                                onClick={() => handleDelete(task.id)}
                                                                disabled={deletingId === task.id}
                                                                title="Delete"
                                                            ><FaTrash /></button>
                                                        </div>
                                                    </div>

                                                    <div className="task-card-title">{task.title}</div>

                                                    {task.description && (
                                                        <div className="task-card-desc">{task.description}</div>
                                                    )}

                                                    {task.feature_title && (
                                                        <div className="task-card-feature">
                                                            <FaLightbulb /> {task.feature_title}
                                                        </div>
                                                    )}

                                                    <div className="task-card-meta">
                                                        {task.assigned_to && (
                                                            <span className="task-assignee">
                                                                <FaUserAlt /> {task.assigned_to}
                                                            </span>
                                                        )}
                                                        {task.due_date && (
                                                            <span className={`task-due ${new Date(task.due_date) < new Date() && task.status !== 'done' ? 'overdue' : ''}`}>
                                                                <FaCalendarAlt /> {formatDate(task.due_date)}
                                                            </span>
                                                        )}
                                                    </div>

                                                    {/* Move buttons */}
                                                    <div className="task-move-btns">
                                                        {col.key !== 'todo' && (
                                                            <button onClick={() => moveStatus(task, col.key === 'in_progress' ? 'todo' : 'in_progress')}>
                                                                ← {col.key === 'in_progress' ? 'To Do' : 'In Progress'}
                                                            </button>
                                                        )}
                                                        {col.key !== 'done' && (
                                                            <button className="move-forward" onClick={() => moveStatus(task, col.key === 'todo' ? 'in_progress' : 'done')}>
                                                                {col.key === 'todo' ? 'Start' : 'Done'} →
                                                            </button>
                                                        )}
                                                    </div>
                                                </motion.div>
                                            ))}
                                        </AnimatePresence>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    /* ─── LIST VIEW ─── */
                    <div className="pay-table-wrap">
                        {filtered.length === 0 ? (
                            <div className="clients-empty">
                                <FaTasks />
                                <h3>No tasks found</h3>
                                <p>Create a task or adjust filters.</p>
                            </div>
                        ) : (
                            <table className="clients-table">
                                <thead>
                                    <tr>
                                        <th>Title</th>
                                        <th>Assigned To</th>
                                        <th>Priority</th>
                                        <th>Due Date</th>
                                        <th>Status</th>
                                        <th>Source</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.map(task => (
                                        <tr key={task.id}>
                                            <td>
                                                <div className="client-full-name">{task.title}</div>
                                                {task.description && <div className="client-email-sub">{task.description.substring(0, 60)}{task.description.length > 60 ? '…' : ''}</div>}
                                            </td>
                                            <td>{task.assigned_to || <span className="task-unassigned">Unassigned</span>}</td>
                                            <td>
                                                <span className={`task-priority-badge ${PRIORITY_META[task.priority]?.cls}`}>
                                                    {PRIORITY_META[task.priority]?.icon} {PRIORITY_META[task.priority]?.label}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={new Date(task.due_date) < new Date() && task.status !== 'done' ? 'task-due overdue' : ''}>
                                                    {formatDate(task.due_date)}
                                                </span>
                                            </td>
                                            <td>
                                                <select
                                                    className="task-status-select"
                                                    value={task.status}
                                                    onChange={e => moveStatus(task, e.target.value)}
                                                >
                                                    <option value="todo">To Do</option>
                                                    <option value="in_progress">In Progress</option>
                                                    <option value="done">Done</option>
                                                </select>
                                            </td>
                                            <td>
                                                {task.feature_title
                                                    ? <span className="task-source-feat"><FaLightbulb /> {task.feature_title.substring(0, 30)}</span>
                                                    : <span className="task-source-manual">Manual</span>}
                                            </td>
                                            <td>
                                                <button className="tbl-btn view" onClick={() => openEdit(task)}><FaEdit /></button>
                                                <button
                                                    className="tbl-btn del"
                                                    onClick={() => handleDelete(task.id)}
                                                    disabled={deletingId === task.id}
                                                ><FaTrash /></button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}

                {/* ─── TASK FORM MODAL ─── */}
                <AnimatePresence>
                    {showForm && (
                        <motion.div
                            className="modal-overlay"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setShowForm(false)}
                        >
                            <motion.div
                                className="task-modal"
                                initial={{ scale: 0.92, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0.92, opacity: 0 }}
                                onClick={e => e.stopPropagation()}
                            >
                                <div className="task-modal-header">
                                    <h2>{editingTask ? 'Edit Task' : 'New Task'}</h2>
                                    <button className="modal-close" onClick={() => setShowForm(false)}><FaTimes /></button>
                                </div>

                                <div className="task-modal-body">
                                    <label className="tm-label">Task Title *</label>
                                    <input
                                        className="tm-input"
                                        placeholder="e.g. Build login page"
                                        value={form.title}
                                        onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                                    />

                                    <label className="tm-label">Description</label>
                                    <textarea
                                        className="tm-input tm-textarea"
                                        placeholder="What needs to be done?"
                                        value={form.description}
                                        onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                                        rows={3}
                                    />

                                    <div className="tm-row">
                                        <div className="tm-field">
                                            <label className="tm-label">Assign To</label>
                                            <input
                                                className="tm-input"
                                                placeholder="Team member name"
                                                value={form.assigned_to}
                                                onChange={e => setForm(f => ({ ...f, assigned_to: e.target.value }))}
                                            />
                                        </div>
                                        <div className="tm-field">
                                            <label className="tm-label">Due Date</label>
                                            <input
                                                type="date"
                                                className="tm-input"
                                                value={form.due_date}
                                                onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))}
                                            />
                                        </div>
                                    </div>

                                    <div className="tm-row">
                                        <div className="tm-field">
                                            <label className="tm-label">Priority</label>
                                            <select
                                                className="tm-input"
                                                value={form.priority}
                                                onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}
                                            >
                                                <option value="high">🔴 High</option>
                                                <option value="medium">🟡 Medium</option>
                                                <option value="low">🟢 Low</option>
                                            </select>
                                        </div>
                                        <div className="tm-field">
                                            <label className="tm-label">Status</label>
                                            <select
                                                className="tm-input"
                                                value={form.status}
                                                onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                                            >
                                                <option value="todo">To Do</option>
                                                <option value="in_progress">In Progress</option>
                                                <option value="done">Done</option>
                                            </select>
                                        </div>
                                    </div>

                                    <label className="tm-label">Link to Feature Request (optional)</label>
                                    <select
                                        className="tm-input"
                                        value={form.feature_request_id}
                                        onChange={e => {
                                            const fid = e.target.value;
                                            const feat = features.find(f => String(f.id) === fid);
                                            setForm(f => ({
                                                ...f,
                                                feature_request_id: fid,
                                                client_id: feat ? feat.client_id : f.client_id,
                                            }));
                                        }}
                                    >
                                        <option value="">— None —</option>
                                        {features.map(f => (
                                            <option key={f.id} value={f.id}>
                                                {f.first_name} {f.last_name} · {f.title}
                                            </option>
                                        ))}
                                    </select>

                                    <label className="tm-label">Link to Client (optional)</label>
                                    <select
                                        className="tm-input"
                                        value={form.client_id}
                                        onChange={e => setForm(f => ({ ...f, client_id: e.target.value }))}
                                    >
                                        <option value="">— None —</option>
                                        {clients.map(c => (
                                            <option key={c.id} value={c.id}>
                                                {c.first_name} {c.last_name} · {c.project_name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="task-modal-footer">
                                    <button className="tm-cancel-btn" onClick={() => setShowForm(false)}>Cancel</button>
                                    <button className="tm-save-btn" onClick={handleSave} disabled={saving}>
                                        {saving ? 'Saving…' : <><FaCheck /> {editingTask ? 'Update Task' : 'Create Task'}</>}
                                    </button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </main>
        </div>
    );
};

export default AdminTasks;
